import { useState } from 'react';
import CallbackModal from '../components/CallbackModal';
import './Contact.css';

const PHONE_DISPLAY = '+91-93192-19252';
const PHONE_TEL = '+919319219252';
const EMAIL = 'info@landmarksconstruction.in';
const ADDRESS = 'G-35, Floor 1, Sector 3, Noida – 201303, Uttar Pradesh, India';
const MAPS_QUERY = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(ADDRESS);
const MAPS_EMBED =
  'https://maps.google.com/maps?q=' + encodeURIComponent(ADDRESS) + '&z=16&hl=en&output=embed';

const MOBILE_RE = /^[6-9]\d{9}$/;

function IconBadgeChat({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden>
      <path
        d="M8 10h8M8 14h5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMail({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M4 6h16v12H4V6zm0 0l8 6 8-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPhone({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M6.5 4.5h3l1.5 4.5-2 1.5c1.2 2.4 3.6 4.8 6 6l1.5-2 4.5 1.5v3c0 .8-.7 1.5-1.5 1.5C8.6 20 4 15.4 4 9.5c0-.8.7-1.5 1.5-1.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPin({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M12 21s7-4.35 7-11a7 7 0 10-14 0c0 6.65 7 11 7 11z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="2.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconClock({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 7v6l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

export default function Contact() {
  const [callbackOpen, setCallbackOpen] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formSuccess, setFormSuccess] = useState(false);
  const [formBusy, setFormBusy] = useState(false);

  const validateForm = (data) => {
    const next = {};
    if (!data.get('fullName')?.trim()) next.fullName = 'Please enter your name.';
    const email = data.get('email')?.trim() || '';
    if (!email) next.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) next.email = 'Please enter a valid email.';
    const mobile = (data.get('mobile') || '').replace(/\D/g, '');
    if (!mobile) next.mobile = 'Please enter your mobile number.';
    else if (!MOBILE_RE.test(mobile)) next.mobile = 'Enter a valid 10-digit Indian mobile (starts 6–9).';
    const message = data.get('message')?.trim() || '';
    if (!message) next.message = 'Please enter a short message.';
    return next;
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    setFormSuccess(false);
    const form = e.target;
    const data = new FormData(form);
    const next = validateForm(data);
    setFormErrors(next);
    if (Object.keys(next).length > 0) return;

    setFormBusy(true);
    const payload = {
      fullName: data.get('fullName')?.trim(),
      email: data.get('email')?.trim(),
      mobile: `+91 ${(data.get('mobile') || '').replace(/\D/g, '')}`,
      message: data.get('message')?.trim(),
    };
    window.setTimeout(() => {
      console.info('[Contact enquiry]', payload);
      setFormBusy(false);
      setFormSuccess(true);
      setFormErrors({});
      form.reset();
    }, 400);
  };

  return (
    <div className="contact-page">
      <CallbackModal open={callbackOpen} onClose={() => setCallbackOpen(false)} />

      <header className="contact-hero" aria-labelledby="contact-hero-heading">
        <div className="contact-hero-bg" aria-hidden="true" />
        <div className="contact-hero-inner">
          <p className="contact-hero-badge">
            <IconBadgeChat className="contact-hero-badge-icon" />
            <span>We are here to help</span>
          </p>
          <h1 id="contact-hero-heading" className="contact-hero-title">
            <span className="contact-hero-title-line">Let&apos;s start a</span>{' '}
            <span className="contact-hero-title-accent">conversation</span>
          </h1>
          <p className="contact-hero-lead">
            Reach Landmarks Construction for site visits, estimates, and project enquiries across Delhi NCR.
            Call, email, or send a message—we will respond as soon as we can.
          </p>
          <button type="button" className="contact-hero-cta" onClick={() => setCallbackOpen(true)}>
            Request a callback
            <span className="contact-hero-cta-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                <path
                  d="M5 12h14M13 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>

          <ul className="contact-hero-pills" aria-label="Quick contact">
            <li className="contact-hero-pill">
              <span className="contact-hero-pill-icon" aria-hidden="true">
                <IconMail />
              </span>
              <a href={`mailto:${EMAIL}`}>Email us</a>
            </li>
            <li className="contact-hero-pill">
              <span className="contact-hero-pill-icon" aria-hidden="true">
                <IconPhone />
              </span>
              <a href={`tel:${PHONE_TEL}`}>Call now</a>
            </li>
            <li className="contact-hero-pill">
              <span className="contact-hero-pill-icon" aria-hidden="true">
                <IconPin />
              </span>
              <span>Delhi NCR</span>
            </li>
          </ul>
        </div>
        <div className="contact-hero-wave" aria-hidden="true">
          <svg viewBox="0 0 1440 96" preserveAspectRatio="none" className="contact-hero-wave-svg">
            <path d="M0,48 C240,12 480,84 720,48 C960,12 1200,84 1440,48 L1440,96 L0,96 Z" />
          </svg>
        </div>
      </header>

      <div className="contact-body">
        <div className="contact-container">
          <div className="contact-grid">
            <div>
              <h2 className="contact-section-title">Contact details</h2>
              <ul className="contact-info-list">
                <li className="contact-info-card">
                  <div className="contact-info-icon" aria-hidden="true">
                    <IconMail />
                  </div>
                  <div className="contact-info-body">
                    <span className="contact-info-label">Email</span>
                    <p className="contact-info-value">
                      <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                    </p>
                  </div>
                </li>
                <li className="contact-info-card">
                  <div className="contact-info-icon" aria-hidden="true">
                    <IconPhone />
                  </div>
                  <div className="contact-info-body">
                    <span className="contact-info-label">Phone</span>
                    <p className="contact-info-value">
                      <a href={`tel:${PHONE_TEL}`}>{PHONE_DISPLAY}</a>
                    </p>
                  </div>
                </li>
                <li className="contact-info-card">
                  <div className="contact-info-icon" aria-hidden="true">
                    <IconPin />
                  </div>
                  <div className="contact-info-body">
                    <span className="contact-info-label">Office</span>
                    <p className="contact-info-value">{ADDRESS}</p>
                  </div>
                </li>
                <li className="contact-info-card">
                  <div className="contact-info-icon" aria-hidden="true">
                    <IconClock />
                  </div>
                  <div className="contact-info-body">
                    <span className="contact-info-label">Office hours</span>
                    <p className="contact-info-value">Monday–Saturday, 9:00–18:00 IST</p>
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="contact-section-title">Send a message</h2>
              <div className="contact-form-panel">
                <form className="contact-form" onSubmit={handleFormSubmit} noValidate>
                  {formSuccess ? (
                    <p className="contact-form-success" role="status">
                      Thank you—your message has been received. We will get back to you shortly.
                    </p>
                  ) : null}
                  <div className="contact-field">
                    <label htmlFor="contact-name">Full name</label>
                    <input id="contact-name" name="fullName" type="text" autoComplete="name" />
                    {formErrors.fullName ? (
                      <p className="contact-field-error">{formErrors.fullName}</p>
                    ) : null}
                  </div>
                  <div className="contact-field">
                    <label htmlFor="contact-email">Email</label>
                    <input id="contact-email" name="email" type="email" autoComplete="email" />
                    {formErrors.email ? <p className="contact-field-error">{formErrors.email}</p> : null}
                  </div>
                  <div className="contact-field">
                    <label htmlFor="contact-mobile">Mobile (10 digits)</label>
                    <input
                      id="contact-mobile"
                      name="mobile"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="9876543210"
                    />
                    {formErrors.mobile ? (
                      <p className="contact-field-error">{formErrors.mobile}</p>
                    ) : null}
                  </div>
                  <div className="contact-field">
                    <label htmlFor="contact-message">Message</label>
                    <textarea id="contact-message" name="message" rows={4} placeholder="How can we help?" />
                    {formErrors.message ? (
                      <p className="contact-field-error">{formErrors.message}</p>
                    ) : null}
                  </div>
                  <button type="submit" className="contact-form-submit" disabled={formBusy}>
                    {formBusy ? 'Sending…' : 'Submit enquiry'}
                  </button>
                </form>
              </div>
            </div>
          </div>

          <section className="contact-map-section" aria-labelledby="contact-map-heading">
            <h2 id="contact-map-heading" className="contact-section-title">
              Visit us
            </h2>
            <div className="contact-map-wrap">
              <iframe
                title="Landmarks Construction office location"
                className="contact-map-iframe"
                src={MAPS_EMBED}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a className="contact-map-link" href={MAPS_QUERY} target="_blank" rel="noopener noreferrer">
              Open in Google Maps
            </a>
          </section>
        </div>
      </div>
    </div>
  );
}
